import { isAfter, isBefore, isSameDay, isWithinInterval } from "date-fns";
import { EventExceptionalClosingDateType, EventScheduleType } from "./dates";
import { MakeKeyNonNull } from "./object";

type EventDates = {
  schedules: Pick<EventScheduleType, "fromDate" | "toDate">[];
  exceptionalClosingDates: Pick<EventExceptionalClosingDateType, "date">[];
};

export const isEventInPast = (dates: EventDates) => {
  if (!dates) return false;
  if (
    dates.schedules &&
    dates.schedules.some(
      (schedule) =>
        isSameDay(schedule.toDate, new Date()) ||
        isAfter(schedule.toDate, new Date())
    )
  )
    return false;
  return !(
    dates.exceptionalClosingDates &&
    dates.exceptionalClosingDates.some(
      (schedule) =>
        isSameDay(schedule.date, new Date()) ||
        isAfter(schedule.date, new Date())
    )
  );
};

export const isTicketingEnabled = <
  TTicketing extends {
    availabilityStartingDate: Date | null;
    availabilityEndingDate: Date | null;
  },
  TEvent extends { ticketing: TTicketing | null },
>(
  event: TEvent
): event is MakeKeyNonNull<TEvent, "ticketing"> =>
  event.ticketing !== null &&
  event.ticketing !== undefined &&
  event.ticketing.availabilityStartingDate !== null &&
  event.ticketing.availabilityEndingDate !== null;

export const isTicketingDateAvailable = <
  TEvent extends {
    ticketing: {
      availabilityStartingDate: Date | null;
      availabilityEndingDate: Date | null;
    } | null;
  },
>(
  event: TEvent
) => {
  const now = new Date();

  const { availabilityStartingDate, availabilityEndingDate } =
    event.ticketing ?? {};

  if (
    availabilityStartingDate !== null &&
    availabilityStartingDate !== undefined &&
    availabilityEndingDate !== null &&
    availabilityEndingDate !== undefined
  ) {
    return isWithinInterval(now, {
      start: availabilityStartingDate,
      end: availabilityEndingDate,
    });
  }

  return false;
};

export const isTicketingAvailable = <
  TEvent extends {
    ticketing: {
      availabilityStartingDate: Date | null;
      availabilityEndingDate: Date | null;
    } | null;
  },
>(
  event: TEvent
) => {
  if (!isTicketingEnabled(event)) {
    return false;
  }

  return isTicketingDateAvailable(event);
};

export const isTicketTypeWithinSalePeriod = (ticketType: {
  saleStartDate: Date | string | null;
  saleEndDate: Date | string | null;
}) => {
  const now = new Date();
  if (ticketType.saleStartDate && isBefore(now, new Date(ticketType.saleStartDate))) {
    return false;
  }
  if (ticketType.saleEndDate && isAfter(now, new Date(ticketType.saleEndDate))) {
    return false;
  }
  return true;
};

export const getEventFirstDate = (dates: EventDates): string | undefined => {
  const firstClassicDate =
    dates.schedules.length > 0 ? dates.schedules[0].fromDate : undefined;
  const firstExceptionDate =
    dates.exceptionalClosingDates.length > 0
      ? dates.exceptionalClosingDates[0].date
      : undefined;
  if (firstClassicDate && firstExceptionDate) {
    return isAfter(firstExceptionDate, firstClassicDate)
      ? firstClassicDate
      : firstExceptionDate;
  }
  return firstClassicDate ?? firstExceptionDate;
};

export const getEventLastDate = (dates: EventDates): string | undefined => {
  const lastClassicDate =
    dates.schedules.length > 0
      ? dates.schedules[dates.schedules.length - 1].toDate
      : undefined;
  const lastExceptionDate =
    dates.exceptionalClosingDates.length > 0
      ? dates.exceptionalClosingDates[dates.exceptionalClosingDates.length - 1]
          .date
      : undefined;
  if (lastClassicDate && lastExceptionDate) {
    return isAfter(lastExceptionDate, lastClassicDate)
      ? lastExceptionDate
      : lastClassicDate;
  }
  return lastClassicDate ?? lastExceptionDate;
};
